// Reads the live audio level and decides three things, on the AUDIO clock (never timers: browsers slow timers in
// background tabs while audio keeps flowing, e.g. while you present slides):
//  - where to cut a chunk: after its minimum length, at the first short pause, or at its maximum;
//  - whether a finished chunk had any speech (silent ones are not sent for transcription);
//  - whether the microphone has gone dead (pure digital zeros: muted by the OS, or access revoked).
// Speech and pauses are judged against the room's own noise floor (the quietest moment of the last minute),
// so a fan doesn't count as speech and a soft speaker isn't a pause. Approach adapted from Prismical (MIT),
// github.com/amicalhq/prismical, packages/silence and app-client/recording/chunker.ts.

export const FRAME_FLOOR = 3e-4; // below this a frame is digital silence and doesn't feed the noise floor
export const SPEECH_OVER_FLOOR = 3.5; // about 11 dB over the floor counts as speech
export const SPEECH_MIN = 0.003; // never call anything quieter than this speech (about -50 dBFS)
export const QUIET_OVER_FLOOR = 2.0;
export const QUIET_MIN = 0.002;
// Ceilings that keep a wrong noise floor safe. If someone talks without a break from the first second, the floor
// learns their voice: then nothing above QUIET_MAX is ever a pause (we just cut at the maximum), and a chunk with
// any frame above LOUD is never skipped, whatever the speech count says. Skipping wrongly loses words; not skipping
// only costs one transcription call.
export const QUIET_MAX = 0.015;
export const LOUD = 0.02;
export const QUIET_WINDOW_S = 0.3; // a pause long enough to cut without slicing a word
export const SILENT_CHUNK_SPEECH_S = 0.4; // a chunk with less speech than this is not transcribed
export const FLOOR_READY_S = 10; // don't judge silence until the floor has this much history
export const DEAD_PEAK = 1e-6;
export const DEAD_S = 4;
export const DEAD_RECOVER_S = 0.5;
const FLOOR_WINDOW_S = 60;

class NoiseFloor {
  constructor() {
    this.buckets = [];
    this.cur = Infinity;
    this.curT = 0;
  }

  push(level, dt) {
    if (level > FRAME_FLOOR) this.cur = Math.min(this.cur, level);
    this.curT += dt;
    if (this.curT >= 1) {
      this.buckets.push(this.cur);
      if (this.buckets.length > FLOOR_WINDOW_S) this.buckets.shift();
      this.cur = Infinity;
      this.curT = 0;
    }
  }

  get value() {
    const m = Math.min(this.cur, ...this.buckets);
    return Number.isFinite(m) ? m : 0;
  }

  get seconds() {
    return this.buckets.length;
  }
}

export function chunkBounds(chunkSeconds) {
  const minS = Math.max(5, Math.round(chunkSeconds * 0.75));
  return { minS, maxS: Math.max(minS + 3, Math.round(chunkSeconds * 1.25)), firstMinS: 6, firstMaxS: 10 };
}

export class SignalTracker {
  constructor({ minS, maxS, firstMinS = 6, firstMaxS = 10 }) {
    Object.assign(this, { minS, maxS, firstMinS, firstMaxS });
    this.floors = { mic: new NoiseFloor(), call: new NoiseFloor() };
    this.quiet = { mic: 0, call: 0 };
    this.speech = { mic: 0, call: 0 };
    this.loudest = { mic: 0, call: 0 };
    this.age = 0;
    this.ordinal = 0;
    this.dead = false;
    this.zeroFor = 0;
    this.liveFor = 0;
  }

  /** One ~100 ms frame: { dt, mic: {rms, peak} | null, call: {rms, peak} | null }. Returns true when it's time to cut. */
  frame({ dt, mic, call }) {
    this.age += dt;
    const inputs = { mic, call };
    for (const k of ["mic", "call"]) {
      const f = inputs[k];
      if (!f) continue;
      const floor = this.floors[k];
      floor.push(f.rms, dt);
      if (f.rms > Math.max(floor.value * SPEECH_OVER_FLOOR, SPEECH_MIN)) this.speech[k] += dt;
      this.loudest[k] = Math.max(this.loudest[k], f.rms);
      const quietBelow = Math.min(Math.max(floor.value * QUIET_OVER_FLOOR, QUIET_MIN), QUIET_MAX);
      this.quiet[k] = f.rms < quietBelow ? this.quiet[k] + dt : 0;
    }
    if (mic) {
      if (mic.peak <= DEAD_PEAK) {
        this.zeroFor += dt;
        this.liveFor = 0;
      } else {
        this.liveFor += dt;
        this.zeroFor = 0;
      }
      if (!this.dead && this.zeroFor >= DEAD_S) this.dead = true;
      else if (this.dead && this.liveFor >= DEAD_RECOVER_S) this.dead = false;
    }
    const [lo, hi] = this.ordinal === 0 ? [this.firstMinS, this.firstMaxS] : [this.minS, this.maxS];
    if (this.age >= hi) return true;
    const paused = ["mic", "call"].every((k) => !inputs[k] || this.quiet[k] >= QUIET_WINDOW_S);
    return this.age >= lo && paused;
  }

  /** Ends the current chunk: its length, and which tracks had no speech worth transcribing. */
  take() {
    const silent = (k) => this.floors[k].seconds >= FLOOR_READY_S && this.speech[k] < SILENT_CHUNK_SPEECH_S && this.loudest[k] < LOUD;
    const out = { seconds: this.age, silent: { mic: silent("mic"), call: silent("call") } };
    this.age = 0;
    this.speech = { mic: 0, call: 0 };
    this.loudest = { mic: 0, call: 0 };
    this.ordinal += 1;
    return out;
  }
}

// Runs on the audio thread: every ~100 ms posts the level of input 0 (microphone) and input 1 (call audio, if any).
// Level is DC-removed (standard deviation), so a mic with a DC offset doesn't read as sound.
const WORKLET = `
class LevelTap extends AudioWorkletProcessor {
  constructor() { super(); this.reset(); this.target = Math.round(sampleRate * 0.1); }
  reset() { this.n = 0; this.s = [0, 0]; this.s2 = [0, 0]; this.pk = [0, 0]; this.has = [false, false]; }
  process(inputs) {
    let len = 128;
    for (let k = 0; k < 2; k++) {
      const ch = inputs[k] && inputs[k][0];
      if (!ch || !ch.length) continue;
      len = ch.length; this.has[k] = true;
      let s = 0, s2 = 0, pk = this.pk[k];
      for (let i = 0; i < ch.length; i++) { const v = ch[i]; s += v; s2 += v * v; const a = v < 0 ? -v : v; if (a > pk) pk = a; }
      this.s[k] += s; this.s2[k] += s2; this.pk[k] = pk;
    }
    this.n += len;
    if (this.n >= this.target) {
      const lvl = (k) => {
        if (!this.has[k]) return null;
        const m = this.s[k] / this.n;
        return { rms: Math.sqrt(Math.max(0, this.s2[k] / this.n - m * m)), peak: this.pk[k] };
      };
      this.port.postMessage({ dt: this.n / sampleRate, mic: lvl(0), call: lvl(1) });
      this.reset();
    }
    return true;
  }
}
registerProcessor("level-tap", LevelTap);
`;

let workletUrl = null;
export const levelWorkletUrl = () => {
  if (!workletUrl) workletUrl = URL.createObjectURL(new Blob([WORKLET], { type: "application/javascript" }));
  return workletUrl;
};
